import api from '@/lib/axios'
import { getMediaUrl } from '@/utils/url-utils'

// Team Management
export interface TeamMember {
    id: string
    name: string
    role: string
    category: string
    photo?: string
    bio?: string
}

export const teamService = {
    getAll: async () => {
        const res = await api.get<TeamMember[]>('/team')
        return res.data.map(m => ({
            ...m,
            photo: getMediaUrl(m.photo)
        }))
    },
    getByCategory: async (category: string) => {
        const res = await api.get<TeamMember[]>(`/team/${category}`)
        return res.data.map(m => ({
            ...m,
            photo: getMediaUrl(m.photo)
        }))
    },
    getById: async (id: string) => {
        const res = await api.get<TeamMember>(`/team/member/${id}`)
        return {
            ...res.data,
            photo: getMediaUrl(res.data.photo)
        }
    },
    create: async (data: Partial<TeamMember>) => {
        const res = await api.post<TeamMember>('/team', data)
        return {
            ...res.data,
            photo: getMediaUrl(res.data.photo)
        }
    },
    update: async (id: string, data: Partial<TeamMember>) => {
        const res = await api.put<TeamMember>(`/team/${id}`, data)
        return {
            ...res.data,
            photo: getMediaUrl(res.data.photo)
        }
    },
    delete: async (id: string) => {
        const res = await api.delete(`/team/${id}`)
        return res.data
    },
}

export interface LocalizedString {
    tr: string
    [key: string]: string
}

// Blog Management
export interface BlogCategory {
    id: string
    _id?: string
    name: LocalizedString
    slug: LocalizedString
    description: LocalizedString
    isActive: boolean
    orderIndex: number
}

export interface BlogTag {
    id: string
    _id?: string
    name: LocalizedString
    slug: LocalizedString
}

export interface BlogPost {
    id: string
    _id?: string
    title: LocalizedString
    slug: LocalizedString
    content: LocalizedString
    excerpt?: LocalizedString
    featuredImage?: string
    isPublished: boolean
    isTranslating?: boolean
    authorId: any
    categories: string[] | BlogCategory[]
    tags: string[] | BlogTag[]
    views?: number
    readingTime?: number
    likes?: number
    seo?: {
        meta_title?: LocalizedString
        meta_description?: LocalizedString
        meta_keywords?: LocalizedString
    }
    createdAt: string
    updatedAt: string
}

export const blogService = {
    getAll: async (params?: { search?: string; page?: number; lang?: string }) => {
        const res = await api.get<{ blogPosts: any[]; pagination: { total: number } }>('/blog', {
            params: { ...params, lang: params?.lang || 'all' }
        })
        return {
            items: res.data.blogPosts.map(p => ({
                ...p,
                id: p._id || p.id,
                featuredImage: getMediaUrl(p.featuredImage)
            })),
            total: res.data.pagination.total
        }
    },
    getBySlug: async (slug: string, lang: string = 'all') => {
        const res = await api.get<BlogPost>(`/blog/${slug}`, { params: { lang } })
        return {
            ...res.data,
            featuredImage: getMediaUrl(res.data.featuredImage)
        }
    },
    getAdminBySlug: async (slug: string, lang: string = 'all') => {
        const res = await api.get<BlogPost>(`/blog/admin/${slug}`, { params: { lang } })
        return {
            ...res.data,
            featuredImage: getMediaUrl(res.data.featuredImage)
        }
    },
    create: async (data: Partial<BlogPost>) => {
        const res = await api.post<BlogPost>('/blog', data)
        return {
            ...res.data,
            featuredImage: getMediaUrl(res.data.featuredImage)
        }
    },
    update: async (id: string, data: Partial<BlogPost>) => {
        const res = await api.put<BlogPost>(`/blog/${id}`, data)
        return {
            ...res.data,
            featuredImage: getMediaUrl(res.data.featuredImage)
        }
    },
    setPublishStatus: async (id: string, isPublished: boolean) => {
        const res = await api.put(`/blog/${id}/publish`, { isPublished })
        return res.data
    },
    delete: async (id: string) => {
        const res = await api.delete(`/blog/${id}`)
        return res.data
    },
    bulkTranslate: async () => {
        const res = await api.post('/blog/bulk-translate')
        return res.data
    }
}

export const blogCategoryService = {
    getAll: async (params?: { all?: boolean; lang?: string }) => {
        const res = await api.get<BlogCategory[]>('/blog-categories', {
            params: { ...params, lang: params?.lang || 'all', all: true }
        })
        return res.data.map(c => ({ ...c, id: c._id || c.id }))
    },
    create: async (data: Partial<BlogCategory>) => {
        const res = await api.post<BlogCategory>('/blog-categories', data)
        return { ...res.data, id: res.data._id || res.data.id }
    },
    update: async (id: string, data: Partial<BlogCategory>) => {
        const res = await api.put<BlogCategory>(`/blog-categories/${id}`, data)
        return { ...res.data, id: res.data._id || res.data.id }
    },
    delete: async (id: string) => {
        const res = await api.delete(`/blog-categories/${id}`)
        return res.data
    },
    bulkTranslate: async () => {
        const res = await api.post('/blog-categories/bulk-translate')
        return res.data
    }
}

export const blogTagService = {
    getAll: async (params?: { all?: boolean; lang?: string }) => {
        const res = await api.get<BlogTag[]>('/blog-tags', {
            params: { ...params, lang: params?.lang || 'all', all: true }
        })
        return res.data.map(t => ({ ...t, id: t._id || t.id }))
    },
    create: async (data: Partial<BlogTag>) => {
        const res = await api.post<BlogTag>('/blog-tags', data)
        return { ...res.data, id: res.data._id || res.data.id }
    },
    update: async (id: string, data: Partial<BlogTag>) => {
        const res = await api.put<BlogTag>(`/blog-tags/${id}`, data)
        return { ...res.data, id: res.data._id || res.data.id }
    },
    delete: async (id: string) => {
        const res = await api.delete(`/blog-tags/${id}`)
        return res.data
    },
    bulkTranslate: async () => {
        const res = await api.post('/blog-tags/bulk-translate')
        return res.data
    }
}

// Forms (Career & Contact)
export interface JobApplication {
    id: string
    name: string
    email: string
    phone: string
    resume: string
    status: 'new' | 'reviewing' | 'accepted' | 'rejected'
    createdAt: string
}

export interface ContactMessage {
    id: string
    name: string
    email: string
    subject: string
    message: string
    status: 'new' | 'in_progress' | 'resolved'
    createdAt: string
}

export const formService = {
    // Career
    getApplications: async () => {
        const res = await api.get<any[]>('/career/applications')
        return {
            items: res.data.map(a => ({
                ...a,
                id: a._id || a.id,
                resumeUrl: getMediaUrl(a.resumeUrl)
            })),
            total: res.data.length
        }
    },

    updateApplicationStatus: async (id: string, status: JobApplication['status']) => {
        const res = await api.put(`/career/applications/${id}/status`, { status })
        return res.data
    },
    getCareerStats: async () => {
        const res = await api.get('/career/stats/overview')
        return res.data
    },

    // Contact
    getMessages: async () => {
        const res = await api.get<{ contactForms: any[]; pagination: { total: number } }>('/contact')
        return {
            items: res.data.contactForms.map(m => ({ ...m, id: m._id || m.id })),
            total: res.data.pagination.total
        }
    },
    updateMessageStatus: async (id: string, status: ContactMessage['status']) => {
        const res = await api.put(`/contact/${id}/status`, { status })
        return res.data
    },
    getContactStats: async () => {
        const res = await api.get('/contact/stats/overview')
        return res.data
    },
    deleteMessage: async (id: string) => {
        const res = await api.delete(`/contact/${id}`)
        return res.data
    },
    deleteApplication: async (id: string) => {
        const res = await api.delete(`/career/applications/${id}`)
        return res.data
    }
}

// Settings
export const settingsService = {
    getSettings: async () => {
        const res = await api.get('/settings')
        return res.data
    },
    updateSetting: async (key: string, value: any) => {
        const res = await api.put(`/settings/${key}`, { value })
        return res.data
    }
}
